import proj4 from 'proj4'
import type { Building } from '@popety-io/types'
// eslint-disable-next-line no-duplicate-imports
import type { Land } from '@popety-io/types'
import { convertWGS84ToLV95 } from '@/services/transport/swissGeoService'
import { convertWGS84ToLV03 } from '@/pages/Analysis/BuildingAnalysis/components/TransportAccessTab/services/swissGeoService'
// Define coordinate systems
proj4.defs(
  'EPSG:2056',
  '+proj=somerc +lat_0=46.9524055555556 +lon_0=7.43958333333333 +k_0=1 +x_0=2600000 +y_0=1200000 +ellps=bessel +towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs +type=crs'
)
proj4.defs(
  'EPSG:3857',
  '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext +no_defs'
)

export const getGeoPortalLink = (entity: Land | Building): string => {
  if (!entity.geo_portal_link && !entity) return ''

  const find = entity.geo_portal_link?.search('{egrid_nb}')

  const regionGeoPortalEgrid =
    find !== -1 && 'egrid_nb' in entity
      ? entity.geo_portal_link?.replace('{egrid_nb}', entity?.egrid_nb || '')
      : null

  const EPSG =
    entity?.geo_portal_link && entity.geo_portal_link.search('{x_3857}') > 0
      ? 'EPSG:3857'
      : 'EPSG:2056'
  const xToReplace = EPSG === 'EPSG:3857' ? '{x_3857}' : '{x}'
  const yToReplace = EPSG === 'EPSG:3857' ? '{y_3857}' : '{y}'

  const coordinates = entity?.geo_center?.lon
    ? proj4('EPSG:4326', EPSG, [entity.geo_center?.lon, entity.geo_center?.lat])
    : undefined

  const x = coordinates ? String(coordinates[0]) : ''
  const y = coordinates ? String(coordinates[1]) : ''

  return (
    regionGeoPortalEgrid ||
    entity?.geo_portal_link
      ?.replace(new RegExp(xToReplace, 'g'), x)
      ?.replace(new RegExp(yToReplace, 'g'), y) ||
    ''
  )
}

export const getFederalPortalLink = (entity: Land | Building): string => {
  const swissCoordinates = convertWGS84ToLV95(
    entity.geo_center?.lat || 0,
    entity.geo_center?.lon || 0
  )
  return `https://map.geo.admin.ch/#/map?center=${swissCoordinates[0]},${swissCoordinates[1]}&z=14&layers=ch.bfs.gebaeude_wohnungs_register;ch.swisstopo-vd.amtliche-vermessung&bgLayer=ch.swisstopo.pixelkarte-farbe`
}

export const getSituationPlanLink = (entity: Land | Building): string => {
  const swissCoordinates = convertWGS84ToLV03(
    entity.geo_center?.lat || 0,
    entity.geo_center?.lon || 0
  )
  return `https://api3.geo.admin.ch/rest/services/ech/MapServer/ch.swisstopo-vd.amtliche-vermessung/3539766/extendedHtmlPopup?lang=en&download_url=https://geodata01.admin.ch/order/jPqrueQazrt/av_pdf.igs?pos=${swissCoordinates[0]}/${swissCoordinates[1]}`
}

export const rdppfLinkByRegion = [
  {
    name: 'St. Gallen',
    link: 'https://oereb.geo.sg.ch/ktsg/wsgi/oereb/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Thurgau',
    link: 'https://map.geo.tg.ch/services/oereb/extract/pdf?EGRID={egrid_nb}&topics=ALL',
  },
  {
    name: 'Solothurn',
    link: 'https://geo.so.ch/api/oereb/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Obwalden',
    link: 'https://oereb.gis-daten.ch/oereb/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Glarus',
    link: 'https://map.geo.gl.ch/oereb/extract/pdf?EGRID={egrid_nb}',
  },
  {
    name: 'Ticino',
    link: 'https://map.geo.ti.ch/oereb2/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Schaffhausen',
    link: 'https://oereb.geo.sh.ch/extract/pdf?EGRID={egrid_nb}',
  },
  {
    name: 'Nidwalden',
    link: 'https://oereb.gis-daten.ch/oereb/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Zug',
    link: 'https://oereb.zg.ch/ors/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Appenzell Ausserrhoden',
    link: 'https://oereb.ar.ch/ktar/wsgi/oereb/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Schwyz',
    link: 'https://map.geo.sz.ch/oereb/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Graubünden',
    link: 'https://oereb.geo.gr.ch/oereb/extract/pdf?EGRID={egrid_nb}',
  },
  {
    name: 'Appenzell Innerrhoden',
    link: 'https://oereb.ai.ch/ktai/wsgi/oereb/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Uri',
    link: 'https://prozessor-oereb.ur.ch/oereb/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Basel-Landschaft',
    link: 'https://oereb.geo.bl.ch/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Bern',
    link: 'https://www.oereb2.apps.be.ch/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Luzern',
    link: 'https://svc.geo.lu.ch/oereb/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Vaud',
    link: 'https://www.rdppf.vd.ch/ws/RdppfSVC.svc/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Zürich',
    link: 'https://maps.zh.ch/oereb/v2/extract/pdf.pdf?EGRID={egrid_nb}',
  },
  {
    name: 'Fribourg',
    link: 'https://geo.fr.ch/RDPPF_ws/RdppfSVC.svc/extract/pdf/?egrid={egrid_nb}',
  },
  {
    name: 'Aargau',
    link: 'https://api.geo.ag.ch/v2/oereb/extract/pdf/?EGRID={egrid_nb}&_dc=&LANG=de',
  },
  {
    name: 'Valais',
    link: 'https://rdppfvs.geopol.ch/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Basel-Stadt',
    link: 'https://api.oereb.bs.ch/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Neuchâtel',
    link: 'https://sitn.ne.ch/crdppf/extract/pdf/?EGRID={egrid_nb}',
  },
  {
    name: 'Jura',
    link: 'https://geo.jura.ch/crdppf_server/extract/pdf?EGRID={egrid_nb}',
  },
  {
    name: 'Genève',
    link: 'https://ge.ch/terecadastrews/RdppfSVC.svc/extract/pdf/?EGRID={egrid_nb}',
  },
]

export const getRddfLandLink = (entity: Land) => {
  const findByRegion = rdppfLinkByRegion.find(
    (region) => region.name === entity.region_name
  )

  if (findByRegion) {
    return findByRegion.link.replace('{egrid_nb}', entity.egrid_nb || '')
  }
}

export const getRddfBuildingLinkForBuilding = (entity: Building) => {
  const findByRegion = rdppfLinkByRegion.find(
    (region) => region.name === entity.region_name
  )

  const egridNumber = entity.lands?.[0]?.egrid_nb || ''

  if (!egridNumber) {
    return
  }

  if (findByRegion) {
    return findByRegion.link.replace('{egrid_nb}', egridNumber)
  }
}
